import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './Admin.css';

const API = '/api/v2';
const COLORS = ['#2563eb','#7c3aed','#059669','#d97706','#dc2626','#0891b2'];

function Admin() {
  const [campaigns, setCampaigns] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [totals, setTotals] = useState({ patients: 0, campaigns: 0, uploads: 0 });
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const cRes = await axios.get(API + '/campaigns');
      const camps = cRes.data.campaigns || [];
      setCampaigns(camps);

      const totalPatients = camps.reduce((s, c) => s + parseInt(c.patient_count || 0), 0);

      // Fetch uploads for all campaigns
      let allUploads = [];
      for (const c of camps) {
        try {
          const uRes = await axios.get(API + '/campaigns/' + c.slug + '/uploads');
          const ups = (uRes.data.uploads || []).map(u => ({ ...u, campaign: c.name }));
          allUploads = allUploads.concat(ups);
        } catch(e) {}
      }
      allUploads.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setUploads(allUploads);

      setTotals({ patients: totalPatients, campaigns: camps.length, uploads: allUploads.length });

      // Fetch analytics for first campaign with patients
      const mainCamp = camps.find(c => c.patient_count > 0);
      if (mainCamp) {
        try {
          const aRes = await axios.get(API + '/campaigns/' + mainCamp.slug + '/analytics');
          setChartData(aRes.data);
        } catch(e) {}
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const timeAgo = (date) => {
    const s = Math.floor((Date.now() - new Date(date)) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return Math.floor(s/60) + 'm ago';
    if (s < 86400) return Math.floor(s/3600) + 'h ago';
    if (s < 604800) return Math.floor(s/86400) + 'd ago';
    return new Date(date).toLocaleDateString();
  };

  if (loading) return <div className="dash-container"><div className="dash-loading">Loading dashboard...</div></div>;

  return (
    <div className="dash-container">
      <div className="dash-header">
        <div>
          <h1>SpineBase Dashboard</h1>
          <p className="dash-sub">Overview of your collaborative spine surgery database</p>
        </div>
      </div>

      <div className="dash-stats">
        <div className="ds-card ds-accent" onClick={() => navigate('/campaigns')}>
          <span className="ds-val">{totals.patients.toLocaleString()}</span>
          <span className="ds-label">Total Patients</span>
        </div>
        <div className="ds-card" onClick={() => navigate('/campaigns')}>
          <span className="ds-val">{totals.campaigns}</span>
          <span className="ds-label">Active Campaigns</span>
        </div>
        <div className="ds-card" onClick={() => navigate('/upload')}>
          <span className="ds-val">{totals.uploads}</span>
          <span className="ds-label">Data Uploads</span>
        </div>
        <div className="ds-card">
          <span className="ds-val">{chartData?.numericStats?.preop_odi?.mean || '—'}</span>
          <span className="ds-label">Avg Pre-op ODI</span>
        </div>
        <div className="ds-card">
          <span className="ds-val">{chartData?.numericStats?.age_at_surgery?.mean || '—'}</span>
          <span className="ds-label">Avg Age</span>
        </div>
        <div className="ds-card">
          <span className="ds-val">{chartData?.numericStats?.bmi?.mean || '—'}</span>
          <span className="ds-label">Avg BMI</span>
        </div>
      </div>

      <div className="dash-grid">
        {/* Campaigns */}
        <div className="dash-panel">
          <div className="dp-header">
            <h2>Campaigns</h2>
            <button className="dp-link" onClick={() => navigate('/campaigns')}>View all →</button>
          </div>
          <div className="camp-list">
            {campaigns.map(c => (
              <div key={c.slug} className="camp-row" onClick={() => navigate('/campaigns')}>
                <div className="cr-info">
                  <span className="cr-name">{c.name}</span>
                  <span className="cr-meta">{c.study_type || 'study'}</span>
                </div>
                <div className="cr-stats">
                  <span className="cr-count">{c.patient_count}</span>
                  <span className="cr-unit">patients</span>
                </div>
              </div>
            ))}
            {campaigns.length === 0 && <p className="empty-msg">No campaigns yet. Create one to get started!</p>}
          </div>
        </div>

        {/* Recent Uploads */}
        <div className="dash-panel">
          <div className="dp-header">
            <h2>Recent Uploads</h2>
            <button className="dp-link" onClick={() => navigate('/upload')}>Upload data →</button>
          </div>
          <div className="upload-list">
            {uploads.slice(0, 8).map(u => (
              <div key={u.id} className="ul-row">
                <div className="ul-icon">📄</div>
                <div className="ul-info">
                  <span className="ul-name">{u.filename}</span>
                  <span className="ul-meta">{u.campaign} · {u.inserted_count || u.row_count} patients · {u.uploaded_by_name}</span>
                </div>
                <span className="ul-time">{timeAgo(u.created_at)}</span>
              </div>
            ))}
            {uploads.length === 0 && <p className="empty-msg">No uploads yet. Upload your first dataset!</p>}
          </div>
        </div>

        {/* Charts */}
        {chartData && chartData.ageHist.length > 0 && (
          <div className="dash-panel">
            <div className="dp-header">
              <h2>Age Distribution</h2>
              <button className="dp-link" onClick={() => navigate('/analytics')}>Full analytics →</button>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData.ageHist}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="range" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Bar dataKey="count" fill="#2563eb" radius={[4,4,0,0]} name="Patients" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {chartData && chartData.distributions.sex && (
          <div className="dash-panel">
            <div className="dp-header">
              <h2>Demographics</h2>
            </div>
            <div className="demo-charts">
              <div className="demo-chart">
                <h4>Sex</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={chartData.distributions.sex} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({name, percent}) => percent > 0.05 ? `${name} ${Math.round(percent*100)}%` : ''} labelLine={false} fontSize={11}>
                      {chartData.distributions.sex.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="demo-chart">
                <h4>Smoking</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={chartData.distributions.smoking_status || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({name, percent}) => percent > 0.05 ? `${name} ${Math.round(percent*100)}%` : ''} labelLine={false} fontSize={11}>
                      {(chartData.distributions.smoking_status || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="dash-panel actions-panel">
          <h2>Quick Actions</h2>
          <div className="qa-grid">
            <button className="qa-btn" onClick={() => navigate('/upload')}><span className="qa-icon">📤</span>Upload Data</button>
            <button className="qa-btn" onClick={() => navigate('/query')}><span className="qa-icon">🔍</span>Query Patients</button>
            <button className="qa-btn" onClick={() => navigate('/analytics')}><span className="qa-icon">📊</span>View Analytics</button>
            <button className="qa-btn" onClick={() => navigate('/campaigns')}><span className="qa-icon">📋</span>Manage Campaigns</button>
          </div>
        </div>
      </div>
    </div>
  );
}
export default Admin;
